import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { environment } from '../../../environments/environment';
import {
  CreateSparkAdDto,
  CreateSparkCampaignDto,
  CreateSparkCampaignResponseDto,
  GetSparkCampaignForBriefAdGroupDto,
  SparkAd,
  TiktokMarketingApiEnvelope,
  UpdateSparkAdStatusDto,
} from '../../common/general-types/tiktok-spark-ads.dto';
import {
  CreateSparkAdGroupRequestDto,
  CreateSparkAdRequestDto,
  CreateSparkAdResponseDto,
} from '../../common/general-types/spark-ads-v3.dto';

@Injectable({
  providedIn: 'root',
})
export class TiktokSparkAdsService {
  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);

  private get headers() {
    return {
      'Content-Type': 'application/json',
    };
  }

  getSparkAds(briefId: string, businessAccountId: string): Observable<SparkAd[]> {
    return this.http.get<SparkAd[]>(
      `${environment.shout_api.url}/tiktok-spark-ads`,
      {
        params: { briefId, businessAccountId },
        headers: this.headers,
      },
    );
  }

  updateSparkAdStatus(dto: UpdateSparkAdStatusDto): Observable<void> {
    return this.http.patch<void>(
      `${environment.shout_api.url}/tiktok-spark-ads/status`,
      dto,
      { headers: this.headers },
    );
  }



  // #########################################################################################
  // #########################################################################################
  // TikTok Spark Ads API - Proper, Official, Human, Good. (TBD)
  // #########################################################################################
  // #########################################################################################


  // #########################################################################################
  // Get Campaign associated with a Brief
  // #########################################################################################
  getSparkCampaignForBrief(briefId: string): Observable<CreateSparkCampaignResponseDto | null> {
    return this.http.get<CreateSparkCampaignResponseDto>(
      `${environment.shout_api.url}/tiktok-spark-ads/get-spark-campaign/${briefId}`,
      {
        headers: this.headers,
      },
    );
  }

  // #########################################################################################
  // Create a Campaign
  // #########################################################################################
  createCampaign(newCampaign: CreateSparkCampaignDto): Observable<CreateSparkCampaignResponseDto> {
    return this.http.post<CreateSparkCampaignResponseDto>(
      `${environment.shout_api.url}/tiktok-spark-ads/create-spark-campaign`,
      newCampaign,
      { headers: this.headers },
    );
  }

  // #########################################################################################
  // Create an Ad-Group
  // #########################################################################################
  createSparkAdGroup(
    newAdGroup: CreateSparkAdGroupRequestDto,
  ): Observable<GetSparkCampaignForBriefAdGroupDto> {
    return this.http.post<GetSparkCampaignForBriefAdGroupDto>(
      `${environment.shout_api.url}/tiktok-spark-ads/create-spark-ad-group`,
      newAdGroup,
      { headers: this.headers },
    );
  }

  // #########################################################################################
  // Create an Ad
  // #########################################################################################
  /**
   * Creates the Spark ad. The API fills in `identity_id` from the stored Spark
   * authorisation code, so it must not be sent from here.
   */
  createSparkAd(newAd: CreateSparkAdRequestDto): Observable<CreateSparkAdResponseDto> {
    return this.http.post<CreateSparkAdResponseDto>(
      `${environment.shout_api.url}/tiktok-spark-ads/create-spark-ad`,
      newAd,
      { headers: this.headers },
    );
  }

  /**
   * Valid location options for ad group targeting (TikTok v1.3 tool/region), via Shout API proxy.
   * @see https://business-api.tiktok.com/portal/docs?id=1737189539571713
   */
  getToolRegion(params: {
    briefId: string;
    advertiserId: string;
    objectiveType: string;
    placements?: string[];
    /** Maps to TikTok `level_range`: ALL | TO_COUNTRY | TO_PROVINCE | TO_CITY | TO_DISTRICT */
    levelRange?: string;
  }): Observable<TiktokMarketingApiEnvelope> {
    let httpParams = new HttpParams()
      .set('briefId', params.briefId)
      .set('advertiserId', params.advertiserId)
      .set('objectiveType', params.objectiveType)
      .set('placements', (params.placements ?? ['PLACEMENT_TIKTOK']).join(','));
    if (params.levelRange) {
      httpParams = httpParams.set('levelRange', params.levelRange);
    }
    return this.http.get<TiktokMarketingApiEnvelope>(`${environment.shout_api.url}/tiktok-spark-ads/tool-region`, {
      params: httpParams,
      headers: this.headers,
    });
  }

  /**
   * Valid language options for ad group targeting (TikTok v1.3 tool/language), via Shout API proxy.
   * @see https://business-api.tiktok.com/portal/docs/get-languages/v1.3
   */
  getToolLanguage(params: { briefId: string; advertiserId: string }): Observable<TiktokMarketingApiEnvelope> {
    const httpParams = new HttpParams()
      .set('briefId', params.briefId)
      .set('advertiserId', params.advertiserId);
    return this.http.get<TiktokMarketingApiEnvelope>(`${environment.shout_api.url}/tiktok-spark-ads/tool-language`, {
      params: httpParams,
      headers: this.headers,
    });
  }

  /**
   * CTA recommendations for ad creatives (TikTok v1.3 tool/creative/cta/recommend), via Shout API proxy.
   * Backend route: `GET /tiktok-spark-ads/tool-creative-cta-recommend`.
   */
  getToolCreativeCtaRecommend(params: {
    businessAccountId: string;
    advertiserId: string;
    assetType: string;
    contentType: string;
  }): Observable<TiktokMarketingApiEnvelope> {
    const httpParams = new HttpParams()
      .set('businessAccountId', params.businessAccountId)
      .set('advertiserId', params.advertiserId)
      .set('assetType', params.assetType)
      .set('contentType', params.contentType);
    return this.http.get<TiktokMarketingApiEnvelope>(
      `${environment.shout_api.url}/tiktok-spark-ads/tool-creative-cta-recommend-standard`,
      {
        params: httpParams,
        headers: this.headers,
      },
    );
  }
}
